/**
 * 轻量确认框：自实现、零依赖、与 toast 风格一致。
 * 返回一个 Promise<boolean>：用户点「确认」resolve(true)，点「取消」/ 点遮罩 / 按 Esc resolve(false)。
 */

interface ConfirmOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
}

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  const {
    title = '提示',
    message,
    confirmText = '确定',
    cancelText = '取消',
  } = opts

  return new Promise<boolean>((resolve) => {
    const overlay = document.createElement('div')
    overlay.className = 'fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4'

    const card = document.createElement('div')
    card.className = 'pointer-events-auto w-full max-w-sm animate-fade-in rounded-xl border border-white/10 bg-slate-800 text-slate-100 shadow-2xl'

    // 标题
    if (title) {
      const t = document.createElement('div')
      t.className = 'px-5 pt-5 text-base font-semibold text-white'
      t.textContent = title
      card.appendChild(t)
    }

    // 内容
    const body = document.createElement('div')
    body.className = 'px-5 py-3 text-sm leading-relaxed text-slate-300'
    body.textContent = message
    card.appendChild(body)

    // 按钮区
    const footer = document.createElement('div')
    footer.className = 'flex justify-end gap-2 border-t border-white/10 px-5 py-3'

    const cancelBtn = document.createElement('button')
    cancelBtn.type = 'button'
    cancelBtn.textContent = cancelText
    cancelBtn.className = 'rounded-lg px-3 py-1.5 text-sm text-slate-300 transition hover:bg-white/5'

    const confirmBtn = document.createElement('button')
    confirmBtn.type = 'button'
    confirmBtn.textContent = confirmText
    confirmBtn.className = 'rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-indigo-500'

    footer.appendChild(cancelBtn)
    footer.appendChild(confirmBtn)
    card.appendChild(footer)
    overlay.appendChild(card)
    document.body.appendChild(overlay)

    let settled = false
    const close = (result: boolean) => {
      if (settled) return
      settled = true
      document.removeEventListener('keydown', onKey)
      overlay.remove()
      resolve(result)
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape')
        close(false)
    }

    cancelBtn.addEventListener('click', () => close(false))
    confirmBtn.addEventListener('click', () => close(true))
    // 点击遮罩（非卡片）视为取消
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay)
        close(false)
    })
    document.addEventListener('keydown', onKey)
  })
}

export default confirmDialog
